import * as React from 'react'
import { styled, keyframes } from 'next-yak'

type AnimateCounterProps = {
  children: React.ReactNode
  id: number | string
}

export function AnimateCounter({ children, id }: AnimateCounterProps): React.ReactElement<AnimateCounterProps> {
  const [state, setState] = React.useState<'stable' | 'transition'>('stable')
  const animatedRef = React.useRef<HTMLDivElement>(null)
  const childrenRef = React.useRef(children)
  const currentIdRef = React.useRef(id)

  React.useEffect(() => {
    if (currentIdRef.current !== id) {
      setState('transition')
    }
  }, [id])

  React.useLayoutEffect(() => {
    if (animatedRef.current !== null) {
      animatedRef.current.addEventListener('animationend', onTransitionEnd)
    }
    return () => {
      if (animatedRef.current !== null) {
        animatedRef.current.removeEventListener('animationend', onTransitionEnd)
      }
    }
  }, [state])

  if (state === 'stable') {
    return <>{childrenRef.current}</>
  }

  return (
    <Wrapper>
      <AnimatedExit>{childrenRef.current}</AnimatedExit>
      <AnimatedEnter ref={animatedRef}>{children}</AnimatedEnter>
    </Wrapper>
  )

  function onTransitionEnd(): void {
    childrenRef.current = children
    currentIdRef.current = id
    setState('stable')
  }
}

const exitTop = keyframes`
  from {
    transform: translateY(0%);
  }
  to {
    transform: translateY(-100%);
  }
`

const enterBottom = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0%);
  }
`

const AnimatedEnter = styled.div`
  animation: ${enterBottom} 0.15s ease-in-out;
`

const AnimatedExit = styled.div`
  animation: ${exitTop} 0.15s ease-in-out;
`

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;

  ${AnimatedEnter} {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
  }
`
